<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ArrowRight, Boxes, Plus, RefreshCw, Search, SlidersHorizontal } from 'lucide-vue-next'
import { inventoryApi, type InventoryGroup, type InventoryItem, type InventoryMovementType } from '@ck/api-client'
import { formatCurrency, formatCurrencyInput, formatDate, formatDateInput, formatQuantity, normalizeDateOnly, parseCurrencyInput } from '@ck/domain'
import { CkAlert, CkButton, CkCard, CkField, CkModal, CkStatTile, CkTable } from '@ck/ui'
import { apiErrorMessage, parseLocaleNumber } from '../lib/operational'
import { emptyInventoryItemDraft, toCreateInventoryItemRequest } from '../lib/inventory-form'

const queryClient = useQueryClient()
const pageSize = 20
const page = ref(1)
const filters = reactive({ q: '', group: '' as InventoryGroup | '', active: true })
const appliedFilters = ref({ q: '', group: '' as InventoryGroup | '', active: true })
const groups: Array<{ value: InventoryGroup; label: string }> = [
  { value: 'mechanics', label: 'Peças mecânicas' },
  { value: 'electrical-ignition', label: 'Elétrica e ignição' },
  { value: 'fluids-lubricants', label: 'Fluidos e lubrificantes' },
  { value: 'brakes-suspension-steering', label: 'Freios, suspensão e direção' },
  { value: 'body-structure', label: 'Funilaria e estrutura' },
  { value: 'paint-finish', label: 'Pintura e acabamento' },
  { value: 'interior', label: 'Interior' },
  { value: 'consumables-ppe', label: 'Consumíveis e EPIs' }
]
const groupLabel = (value: string) => groups.find((group) => group.value === value)?.label ?? value

const itemsQuery = useQuery(computed(() => ({
  queryKey: ['inventory-items', appliedFilters.value, page.value],
  queryFn: () => inventoryApi.listItems({
    q: appliedFilters.value.q || undefined,
    group: appliedFilters.value.group || undefined,
    active: appliedFilters.value.active,
    page: page.value,
    pageSize
  })
})))
const itemsResult = computed(() => itemsQuery.data.value)
const items = computed(() => itemsResult.value?.items ?? [])
const pages = computed(() => Math.max(1, Math.ceil((itemsResult.value?.total ?? 0) / pageSize)))
const selectedItem = ref<InventoryItem | null>(null)
const lotsQuery = useQuery(computed(() => ({
  queryKey: ['inventory-lots', selectedItem.value?.id],
  queryFn: () => inventoryApi.listLots(selectedItem.value!.id),
  enabled: !!selectedItem.value
})))
const lots = computed(() => lotsQuery.data.value?.items ?? [])

function applyFilters() {
  appliedFilters.value = { q: filters.q.trim(), group: filters.group, active: filters.active }
  page.value = 1
}
function selectItem(item: InventoryItem) { selectedItem.value = item }
function refresh() { return queryClient.invalidateQueries({ queryKey: ['inventory-items'] }) }

const itemModal = ref(false)
const itemError = ref<string | null>(null)
const itemForm = reactive(emptyInventoryItemDraft())
function openItemModal() {
  Object.assign(itemForm, emptyInventoryItemDraft())
  itemError.value = null
  itemModal.value = true
}
const createItemMutation = useMutation({
  mutationFn: () => {
    const minimumQuantity = parseLocaleNumber(itemForm.minimumQuantity) ?? 0
    return inventoryApi.createItem(toCreateInventoryItemRequest(itemForm, minimumQuantity))
  },
  onSuccess: async () => { itemModal.value = false; await refresh() },
  onError: (error: unknown) => { itemError.value = apiErrorMessage(error, 'Não foi possível registrar o item.') }
})

const lotModal = ref(false)
const lotError = ref<string | null>(null)
const lotForm = reactive({ lotNumber: '', quantity: '', unitCost: '', receivedOn: formatDateInput(new Date().toISOString().slice(0, 10)), expiresOn: '' })
function openLotModal() {
  if (!selectedItem.value) return
  Object.assign(lotForm, { lotNumber: '', quantity: '', unitCost: '', receivedOn: formatDateInput(new Date().toISOString().slice(0, 10)), expiresOn: '' })
  lotError.value = null
  lotModal.value = true
}
const createLotMutation = useMutation({
  mutationFn: () => {
    if (!selectedItem.value) throw new Error('Selecione um item.')
    const quantity = parseLocaleNumber(lotForm.quantity)
    const unitCost = parseCurrencyInput(lotForm.unitCost)
    const receivedOn = normalizeDateOnly(lotForm.receivedOn)
    const expiresOn = lotForm.expiresOn.trim() ? normalizeDateOnly(lotForm.expiresOn) : null
    if (!lotForm.lotNumber.trim() || !quantity || quantity <= 0 || unitCost === null || unitCost < 0 || !receivedOn || (lotForm.expiresOn.trim() && !expiresOn)) throw new Error('Informe lote, custo e datas válidas.')
    return inventoryApi.createLot({ itemId: selectedItem.value.id, lotNumber: lotForm.lotNumber.trim(), quantity, unitCost, receivedOn, expiresOn })
  },
  onSuccess: async () => { lotModal.value = false; await queryClient.invalidateQueries({ queryKey: ['inventory-lots', selectedItem.value?.id] }); await refresh() },
  onError: (error: unknown) => { lotError.value = apiErrorMessage(error, 'Não foi possível registrar o lote.') }
})

const movementModal = ref(false)
const movementError = ref<string | null>(null)
const movementForm = reactive({ lotId: '', type: 'out' as InventoryMovementType, quantity: '', reason: '' })
function openMovementModal() {
  if (!selectedItem.value) return
  Object.assign(movementForm, { lotId: lots.value.find((lot) => lot.availableQuantity > 0)?.id ?? '', type: 'out', quantity: '', reason: '' })
  movementError.value = null
  movementModal.value = true
}
const createMovementMutation = useMutation({
  mutationFn: () => {
    if (!selectedItem.value) throw new Error('Selecione um item.')
    const quantity = parseLocaleNumber(movementForm.quantity)
    if (!movementForm.lotId || !quantity || quantity <= 0 || !movementForm.reason.trim()) throw new Error('Informe lote, quantidade e motivo.')
    return inventoryApi.createMovement({ itemId: selectedItem.value.id, lotId: movementForm.lotId, type: movementForm.type, quantity, reason: movementForm.reason.trim() })
  },
  onSuccess: async () => { movementModal.value = false; await queryClient.invalidateQueries({ queryKey: ['inventory-lots', selectedItem.value?.id] }); await refresh() },
  onError: (error: unknown) => { movementError.value = apiErrorMessage(error, 'Não foi possível registrar a movimentação.') }
})

const totalAvailable = computed(() => items.value.reduce((sum, item) => sum + item.availableQuantity, 0))
const lowStock = computed(() => items.value.filter((item) => item.availableQuantity <= item.minimumQuantity).length)
</script>

<template>
  <div class="page">
    <header class="page-head">
      <div class="page-head-copy">
        <span class="ck-eyebrow">OPERAÇÃO · ESTOQUE</span>
        <h1>Estoque</h1>
        <p>Catálogo de peças, lotes e movimentações com validade controlada.</p>
      </div>
      <CkButton class="register-button" @click="openItemModal"><Plus :size="17" aria-hidden="true" />Registrar peça</CkButton>
    </header>
    <CkAlert v-if="itemsQuery.isError.value" tone="error">Não foi possível carregar o estoque.</CkAlert>
    <section class="stats" aria-label="Resumo do estoque">
      <CkStatTile :value="formatQuantity(totalAvailable)" label="Unidades disponíveis" tone="accent" />
      <CkStatTile :value="itemsResult?.total ?? 0" label="Itens cadastrados" />
      <CkStatTile :value="lowStock" label="Abaixo do mínimo" :tone="lowStock ? 'fail' : 'ok'" />
    </section>
    <form class="filters" aria-label="Filtros do estoque" @submit.prevent="applyFilters">
      <div class="filter-search">
        <CkField v-model="filters.q" label="Buscar" placeholder="SKU ou descrição" inputmode="search" :maxlength="120" />
      </div>
      <label class="select-field">
        <span>Grupo</span>
        <select v-model="filters.group">
          <option value="">Todos os grupos</option>
          <option v-for="group in groups" :key="group.value" :value="group.value">{{ group.label }}</option>
        </select>
      </label>
      <label class="check-field">
        <input v-model="filters.active" type="checkbox" />
        <span>Somente itens ativos</span>
      </label>
      <div class="filter-actions">
        <CkButton type="submit" variant="secondary"><Search :size="16" aria-hidden="true" />Filtrar</CkButton>
        <CkButton type="button" variant="ghost" aria-label="Atualizar estoque" @click="refresh"><RefreshCw :size="16" aria-hidden="true" /></CkButton>
      </div>
    </form>
    <div class="content-grid">
      <section class="table-wrap" aria-label="Itens de estoque">
        <CkTable
          class="inventory-table"
          :columns="[
            { key: 'sku', label: 'SKU', width: '120px' },
            { key: 'name', label: 'Peça', width: '1.6fr' },
            { key: 'group', label: 'Grupo', width: '1.4fr' },
            { key: 'available', label: 'Disponível', width: '110px', align: 'right' },
            { key: 'unit', label: 'Unidade', width: '90px' },
            { key: 'open', label: '', width: '40px', align: 'right' }
          ]"
          :rows="items"
          :row-key="(row) => row.id"
          empty-message="Nenhuma peça cadastrada."
        >
          <template #default="{ item }">
            <button type="button" class="row-button sku-cell ck-code" data-label="SKU" @click="selectItem(item)">{{ item.sku }}</button>
            <button type="button" class="row-button item-name" data-label="Peça" @click="selectItem(item)">
              <strong>{{ item.name }}</strong>
              <small v-if="item.description">{{ item.description }}</small>
            </button>
            <span class="group-cell" data-label="Grupo">{{ groupLabel(item.group) }}</span>
            <span class="number available-cell" data-label="Disponível" :class="{ low: item.availableQuantity <= item.minimumQuantity }">{{ formatQuantity(item.availableQuantity) }}</span>
            <span class="unit-cell" data-label="Unidade">{{ item.unit }}</span>
            <button type="button" class="open-button" aria-label="Abrir item" @click="selectItem(item)"><ArrowRight :size="16" aria-hidden="true" /></button>
          </template>
        </CkTable>
        <nav v-if="pages > 1" class="pagination" aria-label="Paginação do estoque"><CkButton variant="ghost" :disabled="page <= 1" @click="page--">Anterior</CkButton><span>Página <strong>{{ page }}</strong> de <strong>{{ pages }}</strong></span><CkButton variant="ghost" :disabled="page >= pages" @click="page++">Próxima</CkButton></nav>
      </section>
      <CkCard v-if="selectedItem" class="detail">
        <div class="detail-head">
          <div>
            <span class="ck-eyebrow">ITEM</span>
            <h2>{{ selectedItem.name }}</h2>
            <p class="ck-code">{{ selectedItem.sku }} · {{ groupLabel(selectedItem.group) }}</p>
          </div>
          <button type="button" class="close-detail" aria-label="Fechar detalhe" @click="selectedItem = null">×</button>
        </div>
        <dl class="detail-meta">
          <div><dt>Disponível</dt><dd class="number">{{ formatQuantity(selectedItem.availableQuantity) }} {{ selectedItem.unit }}</dd></div>
          <div><dt>Mínimo</dt><dd class="number">{{ formatQuantity(selectedItem.minimumQuantity) }}</dd></div>
        </dl>
        <p v-if="selectedItem.description" class="description">{{ selectedItem.description }}</p>
        <div class="detail-actions">
          <CkButton variant="secondary" @click="openLotModal"><Plus :size="15" aria-hidden="true" />Novo lote</CkButton>
          <CkButton variant="ghost" @click="openMovementModal"><SlidersHorizontal :size="15" aria-hidden="true" />Movimentar</CkButton>
        </div>
        <div class="lots-heading"><h3>Lotes</h3><span>{{ lots.length }}</span></div>
        <div v-if="lotsQuery.isPending.value" class="empty">Carregando lotes…</div>
        <div v-else-if="lots.length === 0" class="empty">Nenhum lote registrado.</div>
        <ul v-else class="lots">
          <li v-for="lot in lots" :key="lot.id">
            <div>
              <strong>{{ lot.lotNumber }}</strong>
              <small>Recebido {{ formatDate(lot.receivedOn) }}<span v-if="lot.expiresOn"> · vence {{ formatDate(lot.expiresOn) }}</span> · custo {{ formatCurrency(lot.unitCost) }}</small>
            </div>
            <span class="number" :class="{ low: lot.availableQuantity <= 0 }">{{ formatQuantity(lot.availableQuantity) }}</span>
          </li>
        </ul>
      </CkCard>
      <CkCard v-else class="detail empty-detail">
        <Boxes class="empty-icon" :size="28" aria-hidden="true" />
        <h2>Selecione uma peça</h2>
        <p>Abra um item para consultar lotes e registrar entradas ou saídas.</p>
      </CkCard>
    </div>
    <CkModal v-if="itemModal" title="Registrar peça" @close="itemModal = false">
      <form class="modal-form" @submit.prevent="createItemMutation.mutate()">
        <CkAlert v-if="itemError" tone="error">{{ itemError }}</CkAlert>
        <div class="form-grid">
          <CkField v-model="itemForm.sku" label="SKU" required :maxlength="40" />
          <CkField v-model="itemForm.name" label="Nome da peça" required :maxlength="120" />
          <label class="select-field"><span>Grupo</span><select v-model="itemForm.group"><option v-for="group in groups" :key="group.value" :value="group.value">{{ group.label }}</option></select></label>
          <CkField v-model="itemForm.unit" label="Unidade" placeholder="Ex.: un, litro, kg" required :maxlength="20" />
          <CkField v-model="itemForm.minimumQuantity" label="Estoque mínimo" inputmode="decimal" placeholder="0" :maxlength="12" />
        </div>
        <CkField v-model="itemForm.description" label="Descrição (opcional)" :maxlength="180" />
        <div class="form-actions"><CkButton type="button" variant="ghost" @click="itemModal = false">Cancelar</CkButton><CkButton type="submit" :disabled="createItemMutation.isPending.value">Salvar peça</CkButton></div>
      </form>
    </CkModal>
    <CkModal v-if="lotModal" title="Adicionar lote" @close="lotModal = false">
      <form class="modal-form" @submit.prevent="createLotMutation.mutate()">
        <CkAlert v-if="lotError" tone="error">{{ lotError }}</CkAlert>
        <p class="modal-copy">Item selecionado: <strong>{{ selectedItem?.name }}</strong></p>
        <CkField v-model="lotForm.lotNumber" label="Lote" required :maxlength="60" />
        <div class="form-grid">
          <CkField v-model="lotForm.quantity" label="Quantidade recebida" inputmode="decimal" placeholder="0" required :maxlength="12" />
          <CkField :model-value="lotForm.unitCost" label="Custo unitário (R$)" inputmode="decimal" placeholder="R$ 0,00" required :maxlength="20" @update:model-value="lotForm.unitCost = formatCurrencyInput($event)" />
        </div>
        <div class="form-grid">
          <CkField :model-value="lotForm.receivedOn" label="Recebido em" placeholder="dd/MM/yyyy" inputmode="numeric" :maxlength="10" @update:model-value="lotForm.receivedOn = formatDateInput($event)" />
          <CkField :model-value="lotForm.expiresOn" label="Vencimento (opcional)" placeholder="dd/MM/yyyy" inputmode="numeric" :maxlength="10" @update:model-value="lotForm.expiresOn = formatDateInput($event)" />
        </div>
        <div class="form-actions"><CkButton type="button" variant="ghost" @click="lotModal = false">Cancelar</CkButton><CkButton type="submit" :disabled="createLotMutation.isPending.value">Adicionar lote</CkButton></div>
      </form>
    </CkModal>
    <CkModal v-if="movementModal" title="Registrar movimentação" @close="movementModal = false">
      <form class="modal-form" @submit.prevent="createMovementMutation.mutate()">
        <CkAlert v-if="movementError" tone="error">{{ movementError }}</CkAlert>
        <label class="select-field"><span>Tipo</span><select v-model="movementForm.type"><option value="in">Entrada</option><option value="out">Saída</option></select></label>
        <label class="select-field"><span>Lote</span><select v-model="movementForm.lotId"><option v-for="lot in lots" :key="lot.id" :value="lot.id">{{ lot.lotNumber }} · {{ formatQuantity(lot.availableQuantity) }} disponíveis</option></select></label>
        <CkField v-model="movementForm.quantity" label="Quantidade" inputmode="decimal" required :maxlength="12" />
        <CkField v-model="movementForm.reason" label="Motivo" placeholder="Ex.: ajuste de inventário" required :maxlength="180" />
        <div class="form-actions"><CkButton type="button" variant="ghost" @click="movementModal = false">Cancelar</CkButton><CkButton type="submit" :disabled="createMovementMutation.isPending.value">Registrar</CkButton></div>
      </form>
    </CkModal>
  </div>
</template>

<style scoped>
.page { min-width: 0; container: inventory-page / inline-size; }
.page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); margin-bottom: var(--space-5); }
.page-head-copy { min-width: 0; }
.ck-eyebrow { color: var(--ck-amber-400); font-size: var(--fs-2xs); font-weight: var(--fw-bold); letter-spacing: var(--ls-widest); }
h1 { margin: 5px 0 4px; font-size: var(--fs-2xl); font-weight: var(--fw-extrabold); }
.page-head p { margin: 0; color: var(--color-text-muted); font-size: var(--fs-sm); }
.stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--space-4); margin-bottom: var(--space-5); }
.filters { display: grid; grid-template-columns: minmax(220px, 1.5fr) minmax(200px, 1fr) minmax(180px, 1fr) auto; align-items: end; gap: var(--space-3); min-width: 0; margin-bottom: var(--space-4); padding: var(--space-4); border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.filter-search, .select-field, .check-field, .filter-actions { min-width: 0; margin: 0; }
.filters :deep(.ck-field) { margin-bottom: 0; min-width: 0; }
.select-field { display: block; }
.select-field span { display: block; margin-bottom: var(--space-1); color: var(--color-text-muted); font-size: var(--fs-sm); font-weight: var(--fw-semibold); }
.select-field select { width: 100%; min-height: 44px; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); font: inherit; }
.check-field { display: flex; align-items: center; align-self: end; gap: var(--space-2); min-height: 44px; color: var(--color-text-muted); font-size: var(--fs-sm); line-height: var(--lh-normal); }
.check-field input { flex: 0 0 auto; width: 16px; height: 16px; accent-color: var(--ck-orange-500); }
.filter-actions { display: flex; align-items: end; gap: var(--space-2); }
.filter-actions :deep(.ck-button) { flex-shrink: 0; }
.content-grid { display: grid; grid-template-columns: minmax(0, 1.7fr) minmax(300px, .8fr); gap: var(--space-5); align-items: start; }
.table-wrap { min-width: 0; overflow-x: auto; }
.inventory-table :deep(.ck-table) { min-width: 760px; }
.inventory-table :deep(.ck-table-row > *) { min-width: 0; }
.row-button, .open-button { display: block; min-width: 0; width: 100%; padding: 0; border: 0; background: transparent; color: var(--color-text); font: inherit; text-align: left; cursor: pointer; }
.row-button:hover, .row-button:focus-visible, .open-button:hover, .open-button:focus-visible { color: var(--ck-orange-500); }
.item-name { display: flex; flex-direction: column; gap: 2px; }
.item-name small { overflow: hidden; color: var(--color-text-faint); text-overflow: ellipsis; white-space: nowrap; }
.open-button { display: inline-flex; align-items: center; justify-content: flex-end; color: var(--color-text-faint); }
.number { font-family: var(--font-mono); font-size: var(--fs-xs); text-align: right; }
.number.low { color: var(--ck-fail-500); }
.detail { position: sticky; top: var(--space-4); min-width: 0; background: var(--color-surface) !important; border: 1px solid var(--color-border) !important; box-shadow: var(--shadow-md) !important; }
.empty-detail { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 270px; color: var(--color-text-muted); text-align: center; }
.empty-icon { color: var(--ck-amber-400); }
.empty-detail h2 { margin: var(--space-3) 0 var(--space-1); color: var(--color-text); font-size: var(--fs-lg); }
.empty-detail p { max-width: 34ch; margin: 0; color: var(--color-text-muted); font-size: var(--fs-sm); }
.detail-head { display: flex; justify-content: space-between; gap: var(--space-3); min-width: 0; }
.detail-head > div { min-width: 0; }
.detail-head h2 { overflow: hidden; margin: 4px 0 2px; font-size: var(--fs-lg); text-overflow: ellipsis; white-space: nowrap; }
.detail-head p { overflow: hidden; margin: 0; color: var(--color-text-muted); text-overflow: ellipsis; white-space: nowrap; }
.close-detail { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 32px; height: 32px; border: 1px solid var(--color-border); border-radius: var(--radius-full); background: transparent; color: var(--color-text-muted); font-size: 22px; cursor: pointer; }
.close-detail:hover, .close-detail:focus-visible { border-color: var(--ck-orange-500); color: var(--color-text); }
.detail-meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-3); margin: var(--space-5) 0; padding: var(--space-3) 0; border-block: 1px solid var(--color-border); }
.detail-meta dt { color: var(--color-text-faint); font-size: var(--fs-2xs); font-weight: var(--fw-bold); text-transform: uppercase; }
.detail-meta dd { margin: 5px 0 0; color: var(--color-text); }
.description, .empty { color: var(--color-text-muted); font-size: var(--fs-sm); }
.detail-actions { display: flex; gap: var(--space-2); flex-wrap: wrap; }
.lots-heading { display: flex; align-items: center; justify-content: space-between; margin-top: var(--space-5); }
.lots-heading h3 { margin: 0; font-size: var(--fs-md); }
.lots-heading span { color: var(--color-text-faint); font-family: var(--font-mono); }
.lots { display: grid; gap: var(--space-2); margin: var(--space-3) 0 0; padding: 0; list-style: none; }
.lots li { display: flex; justify-content: space-between; gap: var(--space-3); padding: var(--space-3); border: 1px solid var(--color-border); border-radius: var(--radius-md); }
.lots strong, .lots small { display: block; }
.lots small { margin-top: 3px; color: var(--color-text-faint); font-size: var(--fs-2xs); }
.pagination { display: flex; align-items: center; justify-content: center; gap: var(--space-4); margin-top: var(--space-4); color: var(--color-text-muted); font-size: var(--fs-sm); }
.modal-form { display: grid; gap: var(--space-4); min-width: 0; }
.modal-form :deep(.ck-field) { min-width: 0; margin-bottom: 0; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-4); min-width: 0; }
.modal-copy { margin: 0; color: var(--color-text-muted); font-size: var(--fs-sm); }
.form-actions { display: flex; justify-content: flex-end; gap: var(--space-2); margin-top: 0; }

@container inventory-page (max-width: 1100px) {
  .content-grid { grid-template-columns: 1fr; }
  .detail { position: static; }
}

@container inventory-page (max-width: 900px) {
  .filters { grid-template-columns: minmax(0, 1fr) minmax(180px, 1fr); }
  .filter-search, .check-field, .filter-actions { grid-column: span 2; }
  .filter-actions { justify-content: flex-end; }
  .table-wrap { overflow: visible; }
  .inventory-table :deep(.ck-table) { min-width: 0; }
  .inventory-table :deep(.ck-table-head) { display: none; }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head)) {
    grid-template-columns: minmax(0, 1fr) auto !important;
    grid-template-areas:
      "sku open"
      "name name"
      "group group"
      "available unit";
    align-items: start;
    gap: var(--space-3);
    margin: var(--space-3);
    padding: var(--space-4);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg);
  }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) > .sku-cell) { grid-area: sku; }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) > .item-name) { grid-area: name; }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) > .group-cell) { grid-area: group; }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) > .available-cell) { grid-area: available; }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) > .unit-cell) { grid-area: unit; text-align: right; }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) > .open-button) { grid-area: open; width: 36px; }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) [data-label]::before) {
    display: block;
    margin-bottom: var(--space-1);
    color: var(--color-text-faint);
    font-size: var(--fs-2xs);
    font-weight: var(--fw-bold);
    letter-spacing: var(--ls-wide);
    text-transform: uppercase;
  }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) [data-label]::before) { content: attr(data-label); }
  .inventory-table :deep(.ck-table-row:not(.ck-table-head) .sku-cell::before) { color: var(--ck-amber-400); }
}

@container inventory-page (max-width: 600px) {
  .page-head { flex-direction: column; }
  .page-head :deep(.register-button) { width: 100%; }
  .stats { grid-template-columns: 1fr; }
  .filters { grid-template-columns: 1fr; }
  .filter-search, .check-field, .filter-actions { grid-column: auto; }
  .filter-actions { display: grid; grid-template-columns: minmax(0, 1fr) 44px; }
  .filter-actions :deep(.ck-button:first-child) { width: 100%; }
}

@media (max-width: 600px) {
  .form-grid { grid-template-columns: 1fr; }
  .form-actions { flex-wrap: wrap; }
  .form-actions :deep(.ck-button) { flex: 1 1 140px; }
}
</style>
